import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { TableSearch } from 'src/components/shared/table/TableSearch'

interface Props {
  search: string
  partner: string | null
  partners: string[]
  canBulkApprove: boolean
  /** False without `payments_review_decide`: "Approve all visible…" is not rendered. */
  showBulkApprove: boolean
  onSearchChange(search: string): void
  onPartnerChange(partner: string | null): void
  onRefresh(): void
  onBulkApprove(): void
}

const ALL_PARTNERS_VALUE = ''

const PaymentsReviewToolbarComponent = ({
  search,
  partner,
  partners,
  canBulkApprove,
  showBulkApprove,
  onSearchChange,
  onPartnerChange,
  onRefresh,
  onBulkApprove,
}: Props) => {
  // The queue carries `partner` as a NAME (not an id), so the filter options come from the rows.
  const partnerOptions = useMemo(() => {
    const options = partners.map((partnerName) => {
      return { value: partnerName, label: partnerName }
    })

    return [{ value: ALL_PARTNERS_VALUE, label: 'All partners' }, ...options]
  }, [partners])

  const handlePartnerChange = useCallback(
    (value: string | number) => {
      const nextPartner = String(value)

      onPartnerChange(nextPartner === ALL_PARTNERS_VALUE ? null : nextPartner)
    },
    [onPartnerChange],
  )

  return (
    <ToolbarRow>
      <TableSearch
        searchQuery={search}
        onChangeSearchQuery={onSearchChange}
        debounceMs={150}
        width={280}
        inputDataTestId="payments-review-search"
      />
      <div css={tw`flex w-[220px] flex-col`}>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Partner
        </Typography>
        <ProtoSelectStandalone
          value={partner ?? ALL_PARTNERS_VALUE}
          options={partnerOptions}
          placeholder="All partners"
          searchable
          searchPlaceholder="Search partners…"
          onValueChange={handlePartnerChange}
        />
      </div>
      <div css={tw`ml-auto flex flex-row gap-2 pb-1`}>
        <Button
          size="small"
          color="white"
          leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
          onClick={onRefresh}
          data-testid="payments-review-refresh"
        >
          Refresh
        </Button>
        {showBulkApprove ? (
          <Button
            size="small"
            color="dark"
            disabled={!canBulkApprove}
            onClick={onBulkApprove}
            data-testid="payments-review-bulk-approve"
          >
            Approve all visible…
          </Button>
        ) : null}
      </div>
    </ToolbarRow>
  )
}

export const PaymentsReviewToolbar = memo(PaymentsReviewToolbarComponent)
